import { useState, useEffect } from "react";
import { App } from "antd";
import { generateReport } from "@/api/reports";

/**
 * useReportGeneration — отвечает за генерацию данных отчёта по фильтрам.
 *
 * Параметры:
 *  - reportId: uuid отчёта
 *  - reportData: конфигурация отчёта (используется для валидации обязательных фильтров)
 *
 * Возвращает:
 *  - sectionsData: сгенерированные секции (таблицы/текст)
 *  - pending: флаг процесса генерации
 *  - generate(filterValues): запуск генерации
 */
export const useReportGeneration = (reportId, reportData) => {
  const { message } = App.useApp();
  const [sectionsData, setSectionsData] = useState(null);
  const [pending, setPending] = useState(false);

  // Проверка обязательных фильтров перед запросом на генерацию
  const validateRequiredFilters = (filters, filterValues) => {
    const requiredFilters = filters.filter((f) => f.isRequired);
    const missingFilters = requiredFilters.filter((filter) => {
      const value = filterValues[filter.name];
      return (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    if (missingFilters.length > 0) {
      const missingTitles = missingFilters.map((f) => f.title).join(", ");
      message.warning(`Заполните обязательные поля: ${missingTitles}`);
      return false;
    }

    return true;
  };

  const generate = async (filterValues) => {
    // Безопасная проверка, если конфигурация отчёта ещё не загружена
    if (!reportData || !Array.isArray(reportData.filters)) {
      message.warning("Конфигурация отчёта ещё загружается");
      return;
    }
    if (!validateRequiredFilters(reportData.filters, filterValues)) {
      return;
    }

    setPending(true);
    try {
      const result = await generateReport({ uuid: reportId, filterValues });
      setSectionsData(result);
    } catch (error) {
      message.error(error?.message || "Ошибка генерации отчета");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    // При смене отчета очищаем предыдущие данные секций
    setSectionsData(null);
  }, [reportId]);

  return { sectionsData, pending, generate };
};
